<script setup lang="ts">
import {
  computed,
  nextTick,
  onBeforeUnmount,
  onMounted,
  ref,
  watch,
} from "vue";
import { RouterView } from "vue-router";
import {
  pageRouteKey,
  languageSwitchState,
} from "@/utils/pageLanguageNavigation";
import { useConfigStore } from "@/stores/store.config";
import { useDataStore } from "@/stores/store.data";
import { UserStoreFactory } from "@/stores/user";
import { fetchApiKey } from "@/stores/util.dom";
import { loadPolicyContent } from "@/api/CoreService";
import {
  getGepAiVoiceCallStatus,
  type GepAiVoiceCallStatus,
} from "@/api/VideoCallerService";
import {
  checkRoomNumber,
  getCustomerDetails,
  registerNotificationApi,
} from "@/api/GepService";
import { apiCallWithRetry } from "@/api/api-call-with-retry";
import {
  configureTokenRefresh,
  refreshAuthenticationToken,
} from "@/api/HTTPService";
import { initRecaptcha } from "./utils/recaptcha";
import { resolveDeepLinkNavigationTarget } from "./utils/itemDeepLink";
import { canonicalHomeQuery, hasVouchQuery } from "./utils/vouchQuery";
import { buggyLinkQuery } from "./utils/buggyDeepLink";
import {
  consumeRoomScanDestination,
  roomScanQuery,
} from "./utils/roomScanDestination";
import { requiresRoomVerification } from "./utils/nativePageAccess";
import { resolveRoomAccess } from "./utils/roomAccess";
import { isRoomFallbackEnabled } from "./utils/roomFallbackFeature";
import {
  bindRoomHintToApiKey,
  clearInvalidRoomVerification,
  clearRoomInvalid,
  reconcileServerRoom,
} from "./utils/roomVerificationState";
import { showRoomValidationNotice } from "./utils/roomValidationNotice";
import { requestRoomVerification } from "./utils/roomVerificationFlow";
import { isGepPwaInstallPromptEnabled } from "./utils/gepPwa";
import {
  hasStoredGuestIdentity,
  isShortcutLaunch,
  markShortcutNamePending,
} from "./utils/shortcutEntry";
import {
  authState,
  markAuthOk,
  markAuthUnavailable,
  resetAuthRecovery,
} from "./utils/authRecovery";
import AuthUnavailable from "@/components/AuthUnavailable.vue";
import { useActivePageRuntime } from "@/composables/usePageRuntime";
import {
  dismissRevBoosterModal,
  resetRevBoosterQueue,
  startRevBoosterTimer,
} from "@/stores/util.services";
import { Growthbook } from "@/utils/growthbook/growthbook";
import {
  processRateUsPrompt,
  queueRateUsTrigger,
  recordRateUsBlocker,
  useRateUsPrompt,
} from "@/composables/useRateUsPrompt";
import {
  isMeaningfulRateUsRoute,
  recordRateUsInteraction,
  startRateUsEngagementTracking,
} from "@/utils/rateUsEngagement";
import {
  initGepTracker,
  resolveGepEndpoint,
  trackPageView,
} from "@/utils/gep-tracker";

import VSpinner from "./components/VSpinner.vue";
import ErrorModal from "./components/ErrorModal.vue";
import IFrameModal from "./components/IFrameModal.vue";
import VLoading from "./components/loadingModal.vue";
import RevBooster from "./components/RevBooster.vue";
import BaseModal from "./components/BaseModal.vue";
import PageLanguageSwitcher from "./components/PageLanguageSwitcher.vue";
import PreferredLanguagePicker from "./components/PreferredLanguagePicker.vue";
import VIcon from "./components/VIcon.vue";
import RateUsModal from "./components/RateUsModal.vue";
import GepAiVoiceCallButton from "./components/GepAiVoiceCallButton.vue";
import router from "./router";

const ready = ref(false);
const apikey = fetchApiKey();
const oneSignalAppId = location.href.includes("app")
  ? import.meta.env.VITE_HOMEPAGE_APP_ID_APP ||
    "dcc2e5ee-c9bb-4c4b-80d3-893980993be1"
  : import.meta.env.VITE_HOMEPAGE_APP_ID ||
    "dcc2e5ee-c9bb-4c4b-80d3-893980993be1";
const oneSignalApiKey = location.href.includes("app")
  ? import.meta.env.VITE_HOMEPAGE_API_KEY
  : import.meta.env.VITE_HOMEPAGE_API_KEY_PORTAL;
const useUserStore = UserStoreFactory(apikey + "_user");
const storeConfig = useConfigStore();
const storeData = useDataStore();
const pageRuntime = useActivePageRuntime();
const rateUsPrompt = useRateUsPrompt();
const routeStable = ref(false);
const recoveryKey = ref(0);
const retryingAuthentication = ref(false);
const bootAuthenticationFailed = ref(false);
const roomValidationStartupFailed = ref(false);
const isDocumentVisible = ref(document.visibilityState === "visible");
let routeStabilityTimer: ReturnType<typeof setTimeout> | undefined;
let stopEngagementTracking: (() => void) | undefined;
let lastRateUsRouteName = "";
const gepAiVoiceStatus = ref<GepAiVoiceCallStatus | null>(null);
let gepAiVoiceScheduleTimer: ReturnType<typeof setTimeout> | undefined;
let gepAiVoiceStatusRequestVersion = 0;

const isGepAiVoiceEnabled = computed(() => {
  return (
    gepAiVoiceStatus.value?.enabled === true &&
    gepAiVoiceStatus.value?.gepEnabled === true &&
    gepAiVoiceStatus.value?.gepAiOnlyEnabled === true &&
    gepAiVoiceStatus.value?.aiAutoAnswerEnabled === true &&
    gepAiVoiceStatus.value?.humanAgentRequestEnabled === false
  );
});

const refreshGepAiVoiceStatus = async () => {
  const requestVersion = ++gepAiVoiceStatusRequestVersion;
  if (gepAiVoiceScheduleTimer) clearTimeout(gepAiVoiceScheduleTimer);
  const merchantId = storeConfig.useUserStore().merchantId?.trim();
  if (!merchantId) {
    gepAiVoiceStatus.value = null;
    return;
  }

  try {
    const status = await getGepAiVoiceCallStatus(merchantId);
    if (
      requestVersion !== gepAiVoiceStatusRequestVersion ||
      merchantId !== storeConfig.useUserStore().merchantId?.trim()
    )
      return;
    gepAiVoiceStatus.value = status;
    if (status.nextTransitionAt) {
      const delay = new Date(status.nextTransitionAt).getTime() - Date.now();
      if (Number.isFinite(delay) && delay > 0) {
        gepAiVoiceScheduleTimer = window.setTimeout(
          () => void refreshGepAiVoiceStatus(),
          delay + 1000,
        );
      }
    }
  } catch (error) {
    if (requestVersion !== gepAiVoiceStatusRequestVersion) return;
    gepAiVoiceStatus.value = null;
    console.warn("AI voice assistance status is unavailable", error);
  }
};

const safeRateUsRoutes = new Set([
  "page_home",
  "page_facilities",
  "page_facilities_detail",
  "page_things_to_do",
  "page_thingstodo_detail",
  "page_promotion",
  "page_hotel_info",
  "page_activity",
  "page_assistance",
  "page_dc",
]);
const isRateUsSafe = computed(
  () =>
    ready.value &&
    isDocumentVisible.value &&
    routeStable.value &&
    safeRateUsRoutes.has(String(router.currentRoute.value.name || "")) &&
    !Object.values(pageRuntime.activeModals.value).some(Boolean) &&
    !pageRuntime.errorModal.value &&
    !pageRuntime.loadingModal.value &&
    !pageRuntime.iFrameModal.value &&
    !pageRuntime.freeModal.value &&
    !showPreferredLanguagePicker.value &&
    !rateUsPrompt.isBlocked.value,
);

const tryRateUsPrompt = () =>
  processRateUsPrompt({
    token: storeConfig.useUserStore().token || "",
    isSafe: () => isRateUsSafe.value,
    prepare: async () => {
      const user = storeConfig.useUserStore();
      const details = await apiCallWithRetry(getCustomerDetails, {
        lang:
          user.currentSelectedLang ||
          String(router.currentRoute.value.query.lang || "") ||
          user.defaultLang ||
          "en",
        token: user.token || "",
      });
      if (!details) throw new Error("Rate Us customer details unavailable");
      return details;
    },
  });

const markRouteStableSoon = () => {
  routeStable.value = false;
  if (routeStabilityTimer) clearTimeout(routeStabilityTimer);
  routeStabilityTimer = setTimeout(() => {
    routeStable.value = true;
  }, 500);
};

configureTokenRefresh(
  async () => {
    const key = storeConfig.apikey || fetchApiKey();
    const userStore = storeConfig.useUserStore();
    await userStore.getToken(key);
    return userStore.token;
  },
  () => storeConfig.useUserStore().merchantId,
  isRoomFallbackEnabled,
);

const retryAuthentication = async () => {
  if (retryingAuthentication.value) return;
  retryingAuthentication.value = true;
  if (bootAuthenticationFailed.value) {
    window.location.reload();
    return;
  }
  try {
    const refreshed = await refreshAuthenticationToken({ userInitiated: true });
    if (!refreshed) throw new Error("Session refresh returned no result");
    markAuthOk();
    recoveryKey.value++;
  } catch {
    markAuthUnavailable();
  } finally {
    retryingAuthentication.value = false;
  }
};

const redirectForRoomAccess = async (
  route: typeof router.currentRoute.value,
  user: ReturnType<ReturnType<typeof UserStoreFactory>>,
): Promise<boolean> => {
  const decision = resolveRoomAccess(
    route,
    user,
    storeConfig.apikey,
    user.merchantId || "",
  );
  if (decision === "allow") return false;
  const lang = user.currentSelectedLang || user.defaultLang || "en";
  if (decision === "fallback-dc") {
    await router.replace({ name: "page_dc", query: { lang } });
  } else {
    await requestRoomVerification({
      lang,
      pending:
        route.name === "page_buggy"
          ? { ...buggyLinkQuery(route.query), to: "buggy", lang }
          : route.query,
    });
  }
  return true;
};

const nativeLanguageSwitcherRoutes = new Set([
  "page_facilities",
  "page_facilities_detail",
  "page_things_to_do",
  "page_thingstodo_detail",
  "page_hotel_info",
]);
const showPageLanguageSwitcher = computed(() =>
  nativeLanguageSwitcherRoutes.has(router.currentRoute.value.name as string),
);
const showPreferredLanguagePicker = computed(() => {
  const user = storeConfig.useUserStore();
  return (
    (router.currentRoute.value.name === "page_home" ||
      router.currentRoute.value.name === "page_dc" ||
      (router.currentRoute.value.name === "page_assistance" &&
        router.currentRoute.value.query.assistanceOption)) &&
    !user.selectedPreferredLang &&
    (user.homepageConfig?.widgetLanguages || []).length > 1
  );
});

const bgImage = computed(() => {
  const url = useUserStore().mainBackgroundImage
    ? useUserStore().mainBackgroundImage
    : undefined;
  return `url(${url})`;
});

async function preload(
  userStore: ReturnType<ReturnType<typeof UserStoreFactory>>,
) {
  const promises = [];

  if (location.search.includes("vc")) {
    promises.push(userStore.preload());
  }

  promises.push(
    loadPolicyContent(userStore.merchantId || "").catch((err) => {
      console.warn("Skipping policyContent load", err?.message || err);
    }),
  );
  promises.push(storeData.setupUserData(userStore.attributes || {}));
  promises.push(storeData.setSharedData({ test2: { total: 200 } }));

  await Promise.all(promises);
}

const registerPushNotification = async (
  userStore: ReturnType<ReturnType<typeof UserStoreFactory>>,
) => {
  const browserInfo = navigator.userAgent.toLowerCase();
  if (browserInfo.includes("iphone") || browserInfo.includes("ipad")) return;

  const notification = window.Notification;
  if (!notification?.requestPermission) {
    if (!localStorage.getItem("homepageNotificationWarning")) {
      localStorage.setItem("homepageNotificationWarning", "1");
    }
    return;
  }

  let permission: NotificationPermission;
  recordRateUsBlocker("notification-permission", true);
  try {
    permission = await notification.requestPermission();
  } finally {
    recordRateUsBlocker("notification-permission", false);
  }
  const oneSignal = (
    window as Window & {
      OneSignal?: { getUserId?: (callback: (id?: string) => void) => void };
    }
  ).OneSignal;
  if (permission !== "granted" || !oneSignal?.getUserId) return;

  oneSignal.getUserId(async (playerId) => {
    if (!playerId) return;
    await registerNotificationApi({
      account: {
        accountType: "oneSignal",
        accountData: {
          playerId,
          appId: oneSignalAppId,
          apiKey: oneSignalApiKey,
        },
        originUrl: location.href,
      },
      token: userStore.token || "",
    });
    localStorage.setItem(
      "pushNotifToken",
      JSON.stringify({ pushNotifToken: playerId }),
    );
  });
};

const startHomeRevBooster = async () => {
  if (router.currentRoute.value.name !== "page_home") return;
  await nextTick();
  const user = storeConfig.useUserStore();
  const languageCount = (user.homepageConfig?.widgetLanguages || []).length;
  if (
    user.revBoosterIsActive &&
    (user.selectedPreferredLang || languageCount <= 1)
  ) {
    startRevBoosterTimer();
  }
};

// Resume after confirmation even when the language URL stays unchanged.
watch(
  () => storeConfig.useUserStore().selectedPreferredLang,
  async (selected) => {
    if (selected) await startHomeRevBooster();
  },
);

const closeRevBooster = async () => {
  const user = storeConfig.useUserStore();
  const booster = user.selectedRevBooster || {};

  // This is the legacy FreeModal `changed` handler: dismissing an interval
  // promotion records it before scheduling the next available one.
  if (booster.timeConfiguration === "TIME" && booster._id) {
    user.setIntervalDuration(booster._id);
    await user.checkIntervalDuration();
  }

  dismissRevBoosterModal();
};

onMounted(async () => {
  console.log("app.version", import.meta.env.VITE_VERSION);
  try {
    await initRecaptcha();
  } catch (error) {
    // A missing/invalid reCAPTCHA sitekey must not abort app boot: tracker
    // init, token bootstrap and data loading below still need to run.
    console.warn("reCAPTCHA init skipped", error);
  }
  await router.isReady();
  const _search = new URLSearchParams(location.search);
  const shouldCanonicalizeVouchQuery = hasVouchQuery(_search);
  const resumingScanScreen =
    router.currentRoute.value.name === "page_error_scan" &&
    (_search.has("pendingAssistanceMerchant") ||
      _search.has("pendingBuggyMerchant")) &&
    !shouldCanonicalizeVouchQuery;
  storeConfig.setApiKey(fetchApiKey());
  const userStore = storeConfig.useUserStore();
  // A Home Screen shortcut opened on a phone with no saved guest (for example
  // an installed iPhone web app) creates a new guest below; ask that guest for a
  // name. Read before prepare()/getToken() write an identity.
  const createsShortcutGuest =
    isGepPwaInstallPromptEnabled() &&
    isShortcutLaunch(_search) &&
    !hasStoredGuestIdentity(storeConfig.apikey, userStore.username);
  await userStore.prepare();
  const hasRoomScan = _search.has("vcuser_room_number") || _search.has("vcenc");
  const candidateRoom =
    typeof userStore.attributes?.user_room_number === "string"
      ? userStore.attributes.user_room_number.trim()
      : "";
  try {
    await userStore.getToken(storeConfig.apikey);
  } catch {
    console.error("Guest session startup failed");
    bootAuthenticationFailed.value = true;
    markAuthUnavailable();
    ready.value = true;
    return;
  }
  if (createsShortcutGuest) markShortcutNamePending();
  if (!resumingScanScreen)
    consumeRoomScanDestination(_search, userStore.merchantId);
  initGepTracker({
    endpoint: resolveGepEndpoint(),
    getAuthContext: () => ({
      merchantId: userStore.merchantId,
      apiKey: apikey,
      widgetToken: userStore.token,
    }),
  });
  router.afterEach(() => {
    trackPageView();
  });
  trackPageView();
  // Voice assistance is optional; its status endpoint must not block the
  // primary Guest Experience startup path.
  void refreshGepAiVoiceStatus();
  try {
    await Growthbook.loadFeatures({ autoRefresh: true });
  } catch (error) {
    console.warn("Guest feature flags unavailable", error);
  }
  const roomFallbackEnabled = isRoomFallbackEnabled();
  const secureRoomVerification =
    userStore.homepageConfig?.secureRoomVerificationEnabled !== false;
  const showRoomValidationRetry = () => {
    bootAuthenticationFailed.value = true;
    roomValidationStartupFailed.value = true;
    markAuthUnavailable();
    ready.value = true;
  };
  let roomCheckOutcome: "found" | "invalid" | "unavailable" | null = null;
  if (roomFallbackEnabled) {
    bindRoomHintToApiKey(
      storeConfig.apikey,
      shouldCanonicalizeVouchQuery && hasRoomScan,
    );
    if (secureRoomVerification && hasRoomScan) {
      try {
        const roomCheck = candidateRoom
          ? await checkRoomNumber({
              token: userStore.token || "",
              roomNumber: candidateRoom,
            })
          : { message: "Room not found" };
        if (roomCheck?.message === "Room found") {
          roomCheckOutcome = "found";
        } else if (roomCheck?.message === "Room not found") {
          roomCheckOutcome = "invalid";
          clearInvalidRoomVerification(
            userStore,
            storeConfig.apikey,
            userStore.merchantId || "",
          );
        } else {
          roomCheckOutcome = "unavailable";
        }
      } catch {
        // A transient validation failure cannot establish room access.
        console.warn("Room validation unavailable", {
          merchantId: userStore.merchantId,
          route: String(router.currentRoute.value.name || ""),
        });
        roomCheckOutcome = "unavailable";
      }
    }
  }
  if (roomCheckOutcome === "unavailable") {
    showRoomValidationRetry();
    return;
  }
  try {
    await preload(userStore);
  } catch {
    console.error("Guest page startup failed");
    bootAuthenticationFailed.value = true;
    markAuthUnavailable();
    ready.value = true;
    return;
  }

  if (resumingScanScreen) {
    ready.value = true;
    return;
  }

  if (roomFallbackEnabled && secureRoomVerification) {
    const lang = userStore.currentSelectedLang || userStore.defaultLang || "en";
    const fallbackToCompendium = async (notice?: "invalid" | "unavailable") => {
      if (notice) showRoomValidationNotice(notice);
      await router.replace({ name: "page_dc", query: { lang } });
      ready.value = true;
    };

    if (roomCheckOutcome === "invalid") {
      await fallbackToCompendium("invalid");
      return;
    }

    const entryRoute = String(router.currentRoute.value.name || "");
    const needsRoomState =
      hasRoomScan ||
      [
        "home",
        "page_home",
        "page_assistance",
        "page_basic",
        "page_activity",
        "page_buggy",
      ].includes(entryRoute);
    if (needsRoomState) {
      try {
        const details = await getCustomerDetails({
          token: userStore.token || "",
          lang,
        });
        reconcileServerRoom(
          userStore,
          details,
          storeConfig.apikey,
          userStore.merchantId || "",
        );
        if (hasRoomScan) {
          const confirmedRoom = details?.custom_attributes?.user_room_number;
          if (
            roomCheckOutcome !== "found" ||
            String(confirmedRoom ?? "") !== candidateRoom
          ) {
            clearInvalidRoomVerification(
              userStore,
              storeConfig.apikey,
              userStore.merchantId || "",
              false,
            );
            await fallbackToCompendium("unavailable");
            return;
          }
          clearRoomInvalid(storeConfig.apikey);
        }
      } catch {
        console.warn("Room profile unavailable", {
          merchantId: userStore.merchantId,
          route: String(router.currentRoute.value.name || ""),
        });
        showRoomValidationRetry();
        return;
      }
    }
  }

  const isAssistanceLink =
    _search.has("assistanceOption") &&
    (_search.get("to") === "assistance" ||
      router.currentRoute.value.name === "page_assistance");
  const isBuggyLink =
    _search.get("to") === "buggy" ||
    router.currentRoute.value.name === "page_buggy";
  // Consume vc* values during preload before removing transient guest data.
  // Resolve the original destination before canonicalization changes the route.
  if (shouldCanonicalizeVouchQuery || isAssistanceLink || isBuggyLink) {
    const canonicalSearch = new URLSearchParams(_search);
    if (isAssistanceLink) canonicalSearch.set("to", "assistance");
    if (isBuggyLink) canonicalSearch.set("to", "buggy");
    await router.replace({
      name: isBuggyLink
        ? "page_buggy"
        : isAssistanceLink
          ? "page_assistance"
          : "page_home",
      query: canonicalHomeQuery(canonicalSearch),
    });
  }

  const activeRoute = router.currentRoute.value;
  if (roomFallbackEnabled) {
    if (await redirectForRoomAccess(activeRoute, userStore)) {
      ready.value = true;
      return;
    }
  } else if (
    requiresRoomVerification(
      activeRoute.name,
      activeRoute.query.source,
      userStore,
      activeRoute.query,
    )
  ) {
    await router.replace({
      name: "page_error_scan",
      query: roomScanQuery(
        activeRoute.name === "page_assistance" ||
          activeRoute.name === "page_buggy"
          ? activeRoute.query
          : {},
        userStore.currentSelectedLang || userStore.defaultLang,
        userStore.merchantId,
      ),
    });
    ready.value = true;
    return;
  }

  const openCatalogueOrders = _search.get("openCatalogueOrders") === "1";
  if (openCatalogueOrders) {
    const currentLang: string =
      _search.get("lang") ||
      userStore.currentSelectedLang ||
      userStore.defaultLang;
    const catalogueRef = (_search.get("catalogueRef") || "").trim();
    const catalogueLaunchTypeRaw = (
      _search.get("catalogueLaunchType") || ""
    ).trim();
    const actionType =
      catalogueLaunchTypeRaw === "launch-inhouse"
        ? "launch-inhouse"
        : "launch-catalogue";
    const originQuery = (_search.get("origin") || "").trim();
    const fallbackOrigin = `${window.location.origin}/page/home?lang=${encodeURIComponent(
      currentLang,
    )}`;
    const origin = originQuery || fallbackOrigin;

    if (!catalogueRef) {
      await router.replace({
        path: "/page/home",
        query: { lang: currentLang },
      });
      ready.value = true;
      return;
    }

    await router.replace({
      name: "page_catalogue",
      query: {
        to: catalogueRef,
        lang: currentLang,
        actionType,
        origin,
        version: "3",
        openOrders: "1",
      },
    });
    ready.value = true;
    return;
  }

  const to = _search.get("to");
  if (to && !isAssistanceLink && !isBuggyLink) {
    const currentLang: string =
      _search.get("lang") ||
      userStore.currentSelectedLang ||
      userStore.defaultLang;
    const activityId = _search.get("activityId");
    const legacyActivityId = _search.get("activityid");
    const resolvedActivityId = activityId || legacyActivityId;
    const categoryId = _search.get("categoryId");
    const legacyCategoryId = _search.get("categoryid");
    const resolvedCategoryId = categoryId || legacyCategoryId;

    const target = await resolveDeepLinkNavigationTarget({
      to,
      activityId: resolvedActivityId,
      categoryId: resolvedCategoryId,
      auth: {
        merchantId: userStore.merchantId || "",
        lang: currentLang,
        token: userStore.token || "",
      },
    });

    await router.replace({
      path: `/page/${target.pageid}`,
      query: target.query,
    });
  }

  ready.value = true;
  lastRateUsRouteName = String(router.currentRoute.value.name || "");
  markRouteStableSoon();
  if (userStore.merchantId && userStore.customerId) {
    const tracker = startRateUsEngagementTracking({
      merchantId: userStore.merchantId,
      customerId: userStore.customerId,
      onEligible: () => queueRateUsTrigger("ENGAGEMENT"),
    });
    stopEngagementTracking = tracker.stop;
  }
  await startHomeRevBooster();
  void registerPushNotification(userStore).catch((error) =>
    console.warn("Push notification registration skipped", error),
  );
});

watch(
  () => router.currentRoute.value.fullPath,
  async () => {
    if (!ready.value) return;
    const route = router.currentRoute.value;
    const routeName = String(route.name || "");
    if (
      routeName !== lastRateUsRouteName &&
      isMeaningfulRateUsRoute(routeName)
    ) {
      recordRateUsInteraction();
    }
    lastRateUsRouteName = routeName;
    markRouteStableSoon();
    const user = storeConfig.useUserStore();
    if (isRoomFallbackEnabled()) {
      if (await redirectForRoomAccess(route, user)) {
        resetRevBoosterQueue();
        return;
      }
    } else if (
      requiresRoomVerification(
        route.name,
        route.query.source,
        user,
        route.query,
      )
    ) {
      resetRevBoosterQueue();
      await router.replace({
        name: "page_error_scan",
        query: roomScanQuery(
          route.name === "page_buggy"
            ? {
                ...buggyLinkQuery(route.query),
                lang: route.query.lang,
                to: "buggy",
              }
            : route.name === "page_assistance"
              ? route.query
              : {},
          user.currentSelectedLang || user.defaultLang,
          user.merchantId,
        ),
      });
      return;
    }
    if (route.name !== "page_home") {
      resetRevBoosterQueue();
      return;
    }
    await startHomeRevBooster();
  },
);

watch(
  () => storeConfig.useUserStore().merchantId,
  () => {
    resetAuthRecovery();
    gepAiVoiceStatus.value = null;
    void refreshGepAiVoiceStatus();
  },
);

watch(
  () => storeConfig.apikey,
  (key, previous) => {
    if (key !== previous) {
      resetAuthRecovery();
      if (isRoomFallbackEnabled()) bindRoomHintToApiKey(key);
    }
  },
);

watch(
  [
    isRateUsSafe,
    rateUsPrompt.isOpen,
    rateUsPrompt.pendingTrigger,
    rateUsPrompt.claimedTrigger,
    rateUsPrompt.displayedClaimId,
    rateUsPrompt.isBlocked,
  ],
  () => void tryRateUsPrompt(),
  { flush: "post" },
);

const handleVisibilityChange = () => {
  isDocumentVisible.value = document.visibilityState === "visible";
  if (isDocumentVisible.value) void tryRateUsPrompt();
};
document.addEventListener("visibilitychange", handleVisibilityChange);

onBeforeUnmount(() => {
  if (routeStabilityTimer) clearTimeout(routeStabilityTimer);
  if (gepAiVoiceScheduleTimer) clearTimeout(gepAiVoiceScheduleTimer);
  stopEngagementTracking?.();
  document.removeEventListener("visibilitychange", handleVisibilityChange);
});
</script>

<template>
  <div>
    <VSpinner v-if="!ready" />
    <AuthUnavailable
      v-if="ready && authState === 'unavailable'"
      :retrying="retryingAuthentication"
      :reason="roomValidationStartupFailed ? 'room' : 'session'"
      :lang="
        String(
          router.currentRoute.value.query.lang ||
            storeConfig.useUserStore().currentSelectedLang ||
            storeConfig.useUserStore().defaultLang ||
            'en',
        )
      "
      @retry="retryAuthentication"
    />
    <RouterView v-slot="{ Component, route }">
      <component
        v-if="ready && authState !== 'unavailable'"
        :is="Component"
        :key="`${pageRouteKey(route)}:${recoveryKey}`"
      />
    </RouterView>
    <p
      v-if="languageSwitchState.failed"
      role="alert"
      class="language-switch-error"
    >
      {{
        storeConfig.useUserStore().currentLang.labels?.Language_change_error ||
        "Unable to change language. Please try again."
      }}
    </p>
    <GepAiVoiceCallButton
      v-if="ready && authState !== 'unavailable'"
      :enabled="isGepAiVoiceEnabled"
      :merchant-id="storeConfig.useUserStore().merchantId"
      :token="storeConfig.useUserStore().token"
      :language="
        storeConfig.useUserStore().currentSelectedLang ||
        storeConfig.useUserStore().defaultLang
      "
    />
    <PageLanguageSwitcher
      v-if="ready && authState !== 'unavailable' && showPageLanguageSwitcher"
    />
    <PreferredLanguagePicker
      v-if="ready && authState !== 'unavailable' && showPreferredLanguagePicker"
    />
    <RateUsModal v-if="ready && authState !== 'unavailable'" />
    <ErrorModal v-if="pageRuntime.errorModal.value" />
    <IFrameModal v-if="pageRuntime.iFrameModal.value" />
    <VLoading v-if="pageRuntime.loadingModal.value" />
    <BaseModal
      :open="pageRuntime.freeModal.value"
      size="large"
      :z-index="70"
      aria-label="Promotion"
      @close="closeRevBooster"
    >
      <div class="rev-booster-modal">
        <button
          class="rev-booster-modal__close"
          type="button"
          aria-label="Close promotion"
          @click="closeRevBooster"
        >
          <VIcon icon="cross-blue" />
        </button>
        <RevBooster />
      </div>
    </BaseModal>
    <div class="background-image" :style="{ backgroundImage: bgImage }">
      <div
        style="
          flex-direction: column;
          display: flex;
          flex: 1 1 0%;
          background-color: rgba(244, 244, 244, 0.5);
        "
      ></div>
    </div>
  </div>
</template>

<style scoped>
.background-image {
  flex-direction: column;
  display: flex;
  flex: 1 1 0%;
  font-family: inherit;
  position: fixed;
  top: 0px;
  left: 0px;
  width: 100%;
  height: 100%;
  z-index: -999;
  background-size: cover;
  background-position: center 0;
}
.rev-booster-modal {
  display: grid;
  gap: 8px;
}
.rev-booster-modal__close {
  display: grid;
  justify-self: end;
  width: 30px;
  height: 30px;
  padding: 5px;
  border: 0;
  border-radius: 50%;
  place-items: center;
  background: #fff;
  cursor: pointer;
}
</style>

<style scoped>
.language-switch-error {
  position: fixed;
  bottom: 72px;
  left: 50%;
  transform: translateX(-50%);
  box-sizing: border-box;
  width: min(390px, calc(100% - 24px));
  padding: 12px;
  background: white;
  color: #b91c1c;
  border: 1px solid currentColor;
  border-radius: 8px;
  z-index: 100;
}
</style>
